<template>
  <section class="screen">
    <button class="floatbtn" type="button" aria-label="تغییر تم" @click="toggleTheme">
      <base-icon name="sun" />
    </button>

    <div class="s-body stack gap-14 center" style="justify-content: center">
      <brand-emblem />

      <!-- Checking the token -->
      <template v-if="status === 'checking'">
        <div class="spinner"></div>
        <div class="stack gap-6 center">
          <h2>در حال بررسی اطلاعات</h2>
          <p>لطفاً چند لحظه صبر کنید…</p>
        </div>
      </template>

      <!-- No token, or the backend refused it -->
      <template v-else-if="status === 'error'">
        <div class="iconcircle warn"><base-icon name="alert" /></div>
        <div class="stack gap-6 center">
          <h2>امکان شروع فرآیند نیست</h2>
          <p>{{ errorMessage }}</p>
        </div>
      </template>

      <!-- Ready to start -->
      <template v-else>
        <div class="stack gap-10 center" style="margin-top: 32px">
          <h1>احراز هویت غیرحضوری</h1>
          <p class="lead">برای تکمیل فرآیند احراز باید مراحل زیر را انجام دهید:</p>
        </div>

        <ol class="stepper">
          <li v-for="(step, index) in steps" :key="step" class="st">
            <span class="num">{{ toFa(index + 1) }}</span>
            <span class="lb">{{ label(step) }}</span>
          </li>
        </ol>

        <div class="note" style="text-align: right">
          <base-icon name="clock" />
          <span>
            ثبت اطلاعات زمان زیادی نمی‌برد. نتیجه‌ی احراز هویت حدود ۱۵ دقیقه بعد، از طریق پیامک و
            توسط سامانه‌ی ثنا به شما اطلاع داده می‌شود.
          </span>
        </div>
      </template>

      <div class="grow"></div>
    </div>

    <div class="s-foot">
      <button v-if="status === 'ready'" class="btn btn-primary pulse" type="button" @click="begin">
        شروع فرآیند
      </button>
      <button
        v-else-if="status === 'error' && canRetry"
        class="btn btn-primary"
        type="button"
        @click="verify"
      >
        <base-icon name="refresh" />
        <span>تلاش مجدد</span>
      </button>

      <support-link @reveal="supportOpen = true" />
    </div>

    <support-sheet :open="supportOpen" @close="supportOpen = false" />
  </section>
</template>

<script>
import BaseIcon from "@/components/BaseIcon.vue";
import BrandEmblem from "@/components/BrandEmblem.vue";
import SupportLink from "@/components/SupportLink.vue";
import SupportSheet from "@/components/SupportSheet.vue";
import { FLOW_ORDER, STEP_LABELS } from "@/constants";
import { toFa } from "@/utils/digits";
import { loadFaceDetector } from "@/utils/face";
import { primeCue } from "@/utils/cue";

const NO_TOKEN_MESSAGE = "برای دسترسی به این سرویس نیازمند توکن دریافت‌شده از قوه‌قضائیه هستید.";

/**
 * Entry screen: exchange the inbound token for a session, then explain the flow.
 *
 * One component with three states rather than three routes, because a failed token check
 * is not somewhere the user navigated to — it is this screen, unable to proceed. Keeping
 * it here also means "retry" costs nothing, since the token is still in the URL.
 */
export default {
  name: "StartView",
  components: { BaseIcon, BrandEmblem, SupportLink, SupportSheet },

  data() {
    return {
      /** "checking" | "ready" | "error" */
      status: "checking",
      errorMessage: "",
      canRetry: false,
      supportOpen: false,
    };
  },

  computed: {
    /** The three things the user actually does; review is not a task to prepare for. */
    steps() {
      return FLOW_ORDER.slice(0, 3);
    },
  },

  created() {
    this.verify();

    // Start pulling the face detector's runtime down now, while the user is reading this
    // screen. It is ~3 MB compressed and the camera step cannot check framing without it;
    // fetched here, it is usually ready before they ever get there. Nothing waits on this
    // — a failure surfaces on the screen that actually needs it.
    loadFaceDetector().catch(() => {});
  },

  methods: {
    toFa,

    label(step) {
      return STEP_LABELS[step] || "";
    },

    async verify() {
      const token = this.$route.query.token;
      const returnUrl = this.$route.query.returnUrl;

      // Coming back after a reload mid-flow: the session is already in localStorage and
      // there is no token in the URL to re-exchange.
      if (!token && this.$store.getters["session/isAuthenticated"]) {
        this.status = "ready";
        return;
      }

      if (!token) {
        this.fail(NO_TOKEN_MESSAGE, false);
        await this.$store.dispatch("session/clear");
        return;
      }

      this.status = "checking";
      try {
        await this.$store.dispatch("session/checkToken", { token, returnUrl });
        this.status = "ready";
      } catch (error) {
        this.fail(this.messageFrom(error), true);
      }
    },

    fail(message, canRetry) {
      this.status = "error";
      this.errorMessage = message;
      this.canRetry = canRetry;
    },

    /** Prefer the backend's own message; fall back to the transport error. */
    messageFrom(error) {
      const fromResponse =
        error && error.response && error.response.data && error.response.data.message;
      return fromResponse || (error && error.message) || "خطای نامشخص";
    },

    begin() {
      // Audio has to be woken from inside a real tap handler — an AudioContext created
      // outside one starts suspended, and this is the last guaranteed tap before the
      // camera steps, where the framing cue plays.
      primeCue();
      this.$router.push({ name: FLOW_ORDER[0] });
    },

    toggleTheme() {
      this.$store.dispatch("ui/toggleTheme");
    },
  },
};
</script>

<style scoped>
.floatbtn {
  position: absolute;
  top: 14px;
  left: 14px;
  z-index: 15;
  width: 38px;
  height: 38px;
  border-radius: 12px;
  border: 1px solid var(--border);
  background: var(--card);
  color: var(--muted-foreground);
  display: grid;
  place-items: center;
  cursor: pointer;
  box-shadow: var(--shadow);
}

.floatbtn >>> svg {
  width: 18px;
  height: 18px;
}

.stepper {
  position: relative;
  display: flex;
  flex-direction: column;
  width: 100%;
  list-style: none;
  margin: 0;
  padding: 0;
}

/* Dashed connector behind the numbers, inset so it starts and ends on a badge. */
.stepper::before {
  content: "";
  position: absolute;
  top: 21px;
  bottom: 21px;
  right: 15px;
  border-right: 2px dashed var(--border);
  z-index: 0;
}

.st {
  position: relative;
  z-index: 1;
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 6px 0;
}

.st .num {
  flex: 0 0 auto;
  width: 30px;
  height: 30px;
  border-radius: 10px;
  background: var(--primary-soft);
  color: var(--primary);
  font-weight: 700;
  display: grid;
  place-items: center;
  font-size: 14px;
  /* Punches a hole in the dashed connector so it reads as passing behind the badge. */
  box-shadow: 0 0 0 3px var(--background);
}

.st .lb {
  font-size: 14.5px;
  font-weight: 700;
  color: var(--foreground);
}

/* A slow breath on the only thing there is to do on this screen.
   Kept deliberately under-done — a shadow that swells and fades, and half a percent of
   scale. This is a government service; a button that throbs reads as an advert, and the
   point is only to say "here", not to nag. The animation runs on the start screen alone:
   every later screen has a single obvious action already. */
@media (prefers-reduced-motion: no-preference) {
  .pulse {
    animation: breathe 3s ease-in-out infinite;
    /* Both properties are composited, so the loop costs no layout or paint work. */
    will-change: transform, box-shadow;
  }

  /* Zero offset on both axes, so the glow sits evenly around all four sides rather than
     pooling underneath; the breath is carried by blur and spread alone. */
  @keyframes breathe {
    0%,
    100% {
      transform: scale(1);
      box-shadow: 0 0 10px -2px var(--primary-glow);
    }
    50% {
      transform: scale(1.005);
      box-shadow: 0 0 24px 3px var(--primary-glow);
    }
  }
}

/* Pressing it ends the conversation; nothing should still be breathing under a tap. */
.pulse:active {
  animation: none;
}
</style>
